import React, {useEffect, useMemo} from 'react';
import styles from './matchEvents.module.css';
import { MatchEventsInterface } from "../../utils/types";

interface GroupedEvents {
	home: MatchEventsInterface[];
	away: MatchEventsInterface[];
}
interface MatchTeamsInterface {
	host_club__logo: string;
	host_club__name: string;
	host_club__id: number;
	guest_club__logo: string;
	guest_club__name: string;
	guest_club__id: number;
}

interface MatchEventsProps {
	matchEvents: MatchEventsInterface[];
	matchTeams: MatchTeamsInterface;
	onScoreUpdate: (score: Score, penaltyShootOutScore: Score, scoreEvents: ScoreEvent[], penaltyShootOutEvents: ScoreEvent[]) => void;
}

interface Score {
	home: number;
	away: number;
}

interface ScoreEvent {
	type: 'goal' | 'owngoal' | 'penalty kick' | 'penalty shoot-out goal';
	team: 'home' | 'away';
	time: number | string;
	playerName: string;
	icon: string;
}

const MatchEvents: React.FC<MatchEventsProps> = ({ matchEvents, matchTeams, onScoreUpdate }) => {
	const homeTeamId = matchTeams.host_club__id;
	const guestTeamId = matchTeams.guest_club__id;

	const calculateScoreAndEvents = (events: MatchEventsInterface[], homeTeamId: number): { score: Score, scoreEvents: ScoreEvent[], penaltyShootOutScore: Score, penaltyShootOutEvents: ScoreEvent[] } => {
		const score = { home: 0, away: 0 };
		const scoreEvents: ScoreEvent[] = [];
		const penaltyShootOutScore = {home: 0, away: 0};
		const penaltyShootOutEvents: ScoreEvent[] = [];

		events.forEach(event => {
			const isHomeTeam = event.fk_persona.some(persona =>
				persona.team.some(team => team.fk_team === homeTeamId)
			);

			const eventIcon = event.fk_event_type[0]?.icon;
			const eventType = event.fk_event_type[0]?.event_type;
			const playerName = event.fk_persona[0]?.last_name || '';

			if (eventType === 'goal' || eventType === 'penalty kick' || eventType === 'owngoal') {
				const team = isHomeTeam ? 'home' : 'away';
				const oppositeTeam = isHomeTeam ? 'away' : 'home';

				if (eventType === 'owngoal') {
					score[oppositeTeam] += 1;
				} else {
					score[team] += 1;
				}

				scoreEvents.push({
					type: eventType,
					team: eventType === 'owngoal' ? oppositeTeam : team,
					time: event.time,
					playerName,
					icon: eventIcon
				});
			}

			if (eventType === 'penalty shoot-out goal') {
				const team = isHomeTeam ? 'home' : 'away';
				penaltyShootOutScore[team] += 1;
				penaltyShootOutEvents.push({
					type: eventType,
					team: team,
					time: event.time,
					playerName,
					icon: eventIcon
				});
			}
		});

		return { score, scoreEvents, penaltyShootOutScore, penaltyShootOutEvents };
	};

	const { score, scoreEvents, penaltyShootOutScore, penaltyShootOutEvents } = useMemo(() => {
		return calculateScoreAndEvents(matchEvents, homeTeamId);
	}, [matchEvents, homeTeamId]);

	useEffect(() => {
		onScoreUpdate(score, penaltyShootOutScore, scoreEvents, penaltyShootOutEvents);
	}, [score, scoreEvents, penaltyShootOutScore, penaltyShootOutEvents, onScoreUpdate]);


	// Группировка событий по периоду и времени
	const groupedEvents = matchEvents.reduce((acc, event) => {
		const period = event.fk_period2match[0]?.fk_period[0]?.period || 'Unknown';
		const time = event.time;
		if (!acc[period]) {
			acc[period] = {};
		}
		if (!acc[period][time]) {
			acc[period][time] = { home: [], away: [] };
		}
		if (event.fk_persona.some(persona => persona.team.some(team => team.fk_team === homeTeamId))) {
			acc[period][time].home.push(event);
		} else {
			acc[period][time].away.push(event);
		}
		return acc;
	}, {} as Record<string, Record<string | number, GroupedEvents>>);

	// Сортировка периодов и времени внутри каждого периода
	const periodOrder = [
		'Before match',
		'First half',
		'Second half',
		'First half of extra time',
		'Second half of extra time',
		'Penalty shoot-out',
		'After match'
	];

	// Функция для получения индекса периода
	const getPeriodIndex = (periodName: string) => {
		const index = periodOrder.indexOf(periodName);
		return index === -1 ? periodOrder.length : index; // Если период не найден, помещаем его в конец
	};

	// Сортировка периодов
	const sortedPeriods = Object.entries(groupedEvents).sort((a, b) => {
		return getPeriodIndex(a[0]) - getPeriodIndex(b[0]);
	});

	return (
		<div className={styles.events}>
			<div className={styles.events__header}>
				<img src={matchTeams?.host_club__logo} alt={matchTeams?.host_club__name} />
				<h2>{score.home} - {score.away}</h2>
				<img src={matchTeams?.guest_club__logo} alt={matchTeams?.guest_club__name} />
			</div>
			{sortedPeriods.reverse().map(([periodName, periodEvents]) => (
				<div key={periodName} className={styles.period__container}>
					<h4>{periodName}</h4>
					{Object.entries(periodEvents).sort((a, b) => {
						// Сортировка времени в обратном порядке
						return Number(b[0]) - Number(a[0]);
					}).map(([time, events]) => (
						<div key={time} className={styles.event__container}>
							<div className={styles.events__time_container}>
								<div className={styles.events__time}>{time}'</div>
							</div>
							<div className={styles.events__container}>
								<div className={styles.HomeArea}>
									{events.home.map((event, index) => (
										<div key={`home-${event.id}`} className={styles.event}>
											{event.fk_persona[0]?.last_name || ''}
											<img
												src={event.fk_event_type[0]?.icon || ''}
												alt={event.fk_event_type[0]?.event_type || ''}
											/>
										</div>
									))}
								</div>
								<div className={styles.SpaceAreaWithEvents}></div>
								<div className={styles.AwayArea}>
									{events.away.map((event, index) => (
										<div key={`away-${event.id}`} className={`${styles.event} ${styles.event__away}`}>
											<img
												src={event.fk_event_type[0]?.icon || ''}
												alt={event.fk_event_type[0]?.event_type || ''}
											/>
											{event.fk_persona[0]?.last_name || ''}
										</div>
									))}
								</div>
							</div>
						</div>
					))}
				</div>
			))}
		</div>
	);
}

export default React.memo(MatchEvents);